import { dashboardRequest } from "../services/dashboardRequest";
import { useDashboardResource } from "../hooks/useDashboardResource";
import { useAuth } from "../contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { DndContext, closestCenter, MouseSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const capitalizeFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const QueueManagement = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const [playerName, setPlayerName] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [isAdding, setIsAdding] = useState(false);
	const reordering = useRef(false);
	const { user } = useAuth();
	const { data, loading, error, refresh, update } = useDashboardResource({
		key: `queue:${user?.id}:${id}`, enabled: !!user?.id,
		load: async ({ signal }) => {
			const gameData = await dashboardRequest('/club/game-link', { method: 'POST', body: { link: id }, signal });
			if (!gameData.game?.id_table) throw new Error('The linked table could not be loaded.');
			const queueData = await dashboardRequest('/table/queue', { method: 'POST', body: {
				table_id: gameData.game.id_table, id_user: user.id,
			}, signal });
			if (!Array.isArray(queueData.queues)) throw new Error('The latest queue could not be loaded.');
			return { game: gameData.game, queue: [...queueData.queues].sort((a, b) => a.position - b.position) };
		},
	});
	const game = data?.game || {};
	const queue = data?.queue || [];
	const setQueue = next => update(current => ({ ...current, queue: typeof next === 'function' ? next(current.queue) : next }));

	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
		useSensor(TouchSensor, { pressDelay: 150, activationConstraint: { distance: 8 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	);

	const fetchGameAndQueue = async () => {
		update(current => current); // Cancel any read started before the local action.
		await refresh();
	};

	const addToQueue = async () => {
		const name = capitalizeFirst(playerName.trim());
		if (!name) {
			alert("Please enter a player name");
			return;
		}
		setIsAdding(true);
		try {
			const response = await dashboardRequest("/table/queue/add", { method: 'POST', body: {
				table_id: game.id_table,
				player_name: name,
				id_user: user.id,
			} });
			if (response.success) {
				setPlayerName("");
				await fetchGameAndQueue();
			} else {
				alert(response.message || "Failed to add player to queue");
			}
		} catch (e) {
			console.error("Failed to add to queue:", e);
			alert("Failed to add player to queue");
		} finally {
			setIsAdding(false);
		}
	};

	const removeFromQueue = async (queueId) => {
		try {
			const response = await dashboardRequest("/table/queue/remove", { method: 'POST', body: {
				queue_id: queueId,
				id_user: user.id,
			} });
			if (response.success) {
				await fetchGameAndQueue();
				return true;
			} else {
				alert(response.message || "Failed to remove player from queue");
				return false;
			}
		} catch (e) {
			console.error("Failed to remove from queue:", e);
			alert("Failed to remove player from queue");
			return false;
		}
	};

	const renumber = (arr) => arr.map((item, idx) => ({ ...item, position: idx + 1 }));

	const persistOrder = async (newQueue) => {
		setIsSaving(true);
		const previousQueue = queue;
		try {
			const order = newQueue.map((q, i) => ({ id: q.id, position: i + 1 }));
			const res = await dashboardRequest("/table/queue/reorder", { method: 'POST', body: {
				table_id: game.id_table,
				id_user: user.id,
				order,
			} });

			if (!res.success) {
				setQueue(previousQueue);
				alert(res.message || "Failed to save new order");
			} else if (Array.isArray(res.queues)) {
				setQueue(res.queues.sort((a, b) => a.position - b.position));
			}
		} catch (e) {
			console.error("Failed to persist order:", e);
			alert("Failed to save new order");
			setQueue(previousQueue);
		} finally {
			setIsSaving(false);
		}
	};

	const saveName = async (queueId, newName) => {
		const name = capitalizeFirst(newName.trim());
		if (!name) {
			alert("Name cannot be empty");
			return false;
		}
		try {
			const res = await dashboardRequest("/table/queue/edit", { method: 'POST', body: {
				queue_id: queueId,
				player_name: name,
				id_user: user.id,
			} });
			if (!res.success) {
				alert(res.message || "Failed to update name");
				return false;
			}
			if (Array.isArray(res.queues)) {
				setQueue(res.queues.sort((a, b) => a.position - b.position));
			} else {
				setQueue((prev) =>
					prev.map((q) => (q.id === queueId ? { ...q, player_name: name } : q))
				);
			}
			return true;
		} catch (e) {
			console.error("Failed to update name:", e);
			alert("Failed to update name");
			return false;
		}
	};

	const handleDragEnd = async (event) => {
		if (reordering.current) return;
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const oldIndex = queue.findIndex((q) => q.id === active.id);
		const newIndex = queue.findIndex((q) => q.id === over.id);
		if (oldIndex === -1 || newIndex === -1) return;

		const moved = arrayMove(queue, oldIndex, newIndex);
		const withPositions = renumber(moved);
		setQueue(withPositions);
		reordering.current = true;
		try { await persistOrder(withPositions); } finally { reordering.current = false; }
	};

	if (loading) {
		return (
			<div className="page-wrapper">
				<div className="page-body">
					<div className="container-xl w-100">
						<div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
							<div className="spinner-border text-primary" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
	if (!data) return <div className="page-wrapper"><div className="page-body container-xl">
		<div className="alert alert-warning" role="alert">{error || 'Queue unavailable.'}</div>
		<button className="btn btn-primary" onClick={refresh}>Retry loading queue</button>
	</div></div>;

	return (
		<div className="page-wrapper">
			{error && <div className="container-xl alert alert-warning" role="alert">The queue could not refresh. The last available queue is still shown. <button className="btn btn-sm" onClick={refresh}>Retry</button></div>}
			<div className="page-header d-print-none">
				<div className="container-xl">
					<div className="row g-2 align-items-center">
						<div className="col">
							<div className="page-pretitle">
								<svg
									className="w-4 h-4 text-gray-800 dark:text-white"
									aria-hidden="true"
									xmlns="http://www.w3.org/2000/svg"
									width="24"
									height="24"
									fill="none"
									viewBox="0 0 24 24"
									onClick={() => navigate(`/game/${id}`)}
									style={{ cursor: "pointer" }}
								>
									<path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M5 12l4-4m-4 4l4 4" />
								</svg>
								<span className="cursor-pointer text-lg text-gray-800" onClick={() => navigate(`/game/${id}`)}>
									Back
								</span>
							</div>
						</div>
						{isSaving && (
							<div className="col-auto">
								<span>Saving order…</span>
							</div>
						)}
					</div>
				</div>
			</div>

			<div className="page-body">
				<div className="container-xl w-100">
					<h1 className="match-heading text-center">Queue Management - Table {game.table_name}</h1>

					<div className="row mb-3">
						<div className="col-12">
							<div className="card">
								<div className="card-body">
									<div className="input-group mb-2">
										<input
											type="text"
											className="form-control"
											placeholder="Enter player name"
											value={playerName}
											onChange={(e) => setPlayerName(capitalizeFirst(e.target.value))}
											autoCapitalize="words"
										/>
										<button className="btn btn-primary" type="button" onClick={addToQueue} disabled={isAdding || isSaving}>
											{isAdding ? (
												<>
													<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
													Adding…
												</>
											) : (
												"Add to Queue"
											)}
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="row">
						<div className="col-12">
							<div className="card">
								<div className="card-header">
									<h3 className="card-title">Queue</h3>
								</div>
								<div className="card-body">
									{queue.length > 0 ? (
										<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
											<SortableContext items={queue.map((q) => q.id)} strategy={verticalListSortingStrategy}>
												<ul className="list-group">
													{queue.map((item) => (
														<SortableRow
															key={item.id}
															item={item}
															onRemove={() => removeFromQueue(item.id)}
															onSaveName={saveName}
															isSavingGlobal={isSaving}
														/>
													))}
												</ul>
											</SortableContext>
										</DndContext>
									) : (
										<p className="text-center">No players in the queue</p>
									)}
								</div>
							</div>

							<style>{`
                .drag-handle {
                  cursor: grab;
                  touch-action: none;
                  user-select: none;
                  border: 0;
                  background: transparent;
                  line-height: 1;
                  font-size: 1.25rem;
                  padding: .25rem .5rem;
                }
                .drag-handle:active { cursor: grabbing; }
                .sortable-row.dragging { opacity: .85; transform: scale(0.98); }
              `}</style>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

function SortableRow({ item, onRemove, onSaveName, isSavingGlobal }) {
	const [isEditing, setIsEditing] = useState(false);
	const [isSavingName, setIsSavingName] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [name, setName] = useState(item.player_name);

	useEffect(() => {
		if (!isEditing) setName(item.player_name);
	}, [item.player_name, isEditing]);

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
		useSortable({ id: item.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	const startEdit = () => setIsEditing(true);
	const cancelEdit = () => {
		setName(item.player_name);
		setIsEditing(false);
	};
	const doSave = async () => {
		setIsSavingName(true);
		const ok = await onSaveName(item.id, name);
		setIsSavingName(false);
		if (ok) setIsEditing(false);
	};
	const onKeyDown = (e) => {
		if (e.key === "Enter") doSave();
		if (e.key === "Escape") cancelEdit();
	};

	const doDelete = async () => {
		setIsDeleting(true);
		await onRemove();
		setIsDeleting(false);
	};

	return (
		<li
			ref={setNodeRef}
			style={style}
			className={`list-group-item d-flex justify-content-between align-items-center sortable-row ${isDragging ? "dragging" : ""}`}
		>
			<div className="d-flex align-items-center flex-grow-1">
				{/* Drag handle right next to the name (hidden when editing) */}
				{!isEditing && (
					<button
						className="drag-handle me-2"
						title="Drag to reorder"
						disabled={isSavingGlobal}
						aria-label={`Drag to reorder ${item.player_name}`}
						{...attributes}
						{...listeners}
					>
						⠿
					</button>
				)}

				{/* Name / Editor */}
				{!isEditing ? (
					<span className="fw-medium text-truncate">
						{item.position}. {item.player_name}
					</span>
				) : (
					<input
						className="form-control"
						value={name}
						onChange={(e) => setName(capitalizeFirst(e.target.value))}
						onKeyDown={onKeyDown}
						autoFocus
						inputMode="text"
						placeholder="Player name"
						style={{ maxWidth: 280 }}
						disabled={isSavingName}
					/>
				)}
			</div>

			{/* Actions */}
			<div className="d-flex align-items-center gap-2 ms-2">
				{!isEditing ? (
					<>
						<button className="btn btn-primary" onClick={startEdit} disabled={isSavingGlobal}>
							Edit
						</button>
						<button className="btn btn-danger" onClick={doDelete} disabled={isSavingGlobal || isDeleting}>
							{isDeleting ? (
								<>
									<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									Deleting…
								</>
							) : (
								"Remove"
							)}
						</button>
					</>
				) : (
					<>
						<button className="btn btn-primary" onClick={doSave} disabled={isSavingName}>
							{isSavingName ? (
								<>
									<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									Saving…
								</>
							) : (
								"Save"
							)}
						</button>
						<button className="btn btn-outline-secondary" onClick={cancelEdit} disabled={isSavingName}>
							Cancel
						</button>
					</>
				)}
			</div>
		</li>
	);
}

// Local editing state belongs to one account/table too, not just its fetched data.
export default function QueueManagementRoute() {
	const { id } = useParams();
	const { user } = useAuth();
	return <QueueManagement key={`${user?.id}:${id}`} />;
}
